import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { useConfirm, useToast } from '@/components/ui';
import { api } from '@/lib/api';
/* Reusable bulk-selection helpers for any list page.
 *
 * Drop the returned `Checkbox` cell on each row, the `HeaderCheckbox`
 * on the header, and the `BulkActionsBar` somewhere visible above the
 * list. The hook handles the per-row select state, the all/none master
 * checkbox, and a multi-DELETE that fans out to the per-row endpoint.
 *
 *     const ids = useBulkSelection(rows);
 *     ids.HeaderCheckbox  → renders header master checkbox
 *     ids.rowCheckbox(id) → renders per-row checkbox
 *     <BulkActionsBar selection={ids} onDeleteEach={(id) => api.delete(`/foo/${id}/`)}/>
 *
 * Reasonable default scope: list pages that have a single DELETE
 * endpoint per row. Pages with bulk delete on the server side can
 * override `onDeleteEach` to call the server endpoint once. */
export function useBulkSelection(rows) {
    const [selected, setSelected] = useState(new Set());
    const allIds = useMemo(() => new Set((rows ?? []).map((r) => r.id)), [rows]);
    const allSelected = allIds.size > 0 && selected.size === allIds.size
        && Array.from(allIds).every((id) => selected.has(id));
    const someSelected = selected.size > 0 && !allSelected;
    const toggleAll = useCallback(() => {
        if (allSelected)
            setSelected(new Set());
        else
            setSelected(new Set(allIds));
    }, [allIds, allSelected]);
    const toggleRow = useCallback((id) => {
        setSelected((cur) => {
            const next = new Set(cur);
            if (next.has(id))
                next.delete(id);
            else
                next.add(id);
            return next;
        });
    }, []);
    return {
        selected,
        setSelected,
        selectedCount: selected.size,
        allSelected,
        someSelected,
        toggleAll,
        toggleRow,
        HeaderCheckbox: (_jsx("input", { type: "checkbox", checked: allSelected, ref: (el) => { if (el)
                el.indeterminate = someSelected; }, onChange: toggleAll, "aria-label": "Select all", className: "h-4 w-4 rounded border-input" })),
        rowCheckbox: (id) => (_jsx("input", { type: "checkbox", checked: selected.has(id), onChange: (e) => { e.stopPropagation(); toggleRow(id); }, onClick: (e) => e.stopPropagation(), "aria-label": "Select row", className: "h-4 w-4 rounded border-input" })),
    };
}
export function BulkActionsBar({ selection, onDeleteEach, onAfter, resourceName = 'items', }) {
    const { confirm } = useConfirm();
    const toast = useToast();
    const [busy, setBusy] = useState(false);
    if (selection.selectedCount === 0)
        return null;
    return (_jsxs("div", { className: "sticky top-0 z-20 flex items-center justify-between gap-3 rounded-md border bg-primary/5 px-3 py-2 text-xs", children: [_jsxs("span", { className: "font-medium text-primary", children: [selection.selectedCount, " ", resourceName, " selected"] }), _jsxs("div", { className: "flex items-center gap-2", children: [_jsx("button", { type: "button", onClick: () => selection.setSelected(new Set()), className: "rounded-md border border-input px-2 py-1 hover:bg-muted", children: "Clear" }), _jsxs("button", { type: "button", disabled: busy, onClick: async () => {
                            const ok = await confirm({
                                title: `Delete ${selection.selectedCount} ${resourceName}?`,
                                description: 'This action cannot be undone. Posted / signed records will be skipped.',
                                confirmLabel: `Delete ${selection.selectedCount}`,
                                tone: 'danger',
                            });
                            if (!ok)
                                return;
                            setBusy(true);
                            const ids = Array.from(selection.selected);
                            let deleted = 0;
                            const failures = [];
                            for (const id of ids) {
                                try {
                                    await onDeleteEach(id);
                                    deleted++;
                                }
                                catch (e) {
                                    failures.push({
                                        id,
                                        reason: e?.response?.data?.error?.message
                                            ?? e?.response?.data?.detail
                                            ?? e?.message ?? 'unknown',
                                    });
                                }
                            }
                            setBusy(false);
                            selection.setSelected(new Set());
                            if (failures.length === 0) {
                                toast.success(`Deleted ${deleted}`);
                            }
                            else if (deleted > 0) {
                                toast.warning(`Deleted ${deleted}, skipped ${failures.length}`, failures[0]?.reason ?? '');
                            }
                            else {
                                toast.error('Nothing deleted', failures[0]?.reason ?? '');
                            }
                            onAfter?.();
                        }, className: "inline-flex items-center gap-1 rounded-md bg-destructive px-2.5 py-1 font-medium text-destructive-foreground disabled:opacity-50", children: [_jsx(Trash2, { className: "h-3.5 w-3.5" }), busy ? 'Deleting…' : `Delete ${selection.selectedCount}`] })] })] }));
}
/* Helper that builds the per-row `DELETE /resource/<id>/` mutator
 * given a resource path like "/sales/customers". */
export function deleteOne(resourcePath) {
    return async (id) => {
        await api.delete(`${resourcePath}/${id}/`);
    };
}
